/**
 * The Glass — the semantic layer's front door (wave glass-1, #790; THE GLASS
 * ruled #786; nav name "The Glass" ruled by Chris 2026-08-30 at wave open).
 *
 * Three sections — IS / DOES / HOW — every element rendered from packaged
 * canon (GLASS_CANON, generated at build time from the canon files this page
 * footnotes; never a committed copy):
 *   IS   — the C4 L1 Context diagram (engineering/docs/diagrams/context.md).
 *   DOES — the bus matrix as a clickable grid
 *          (engineering/contract/bus-matrix.yaml).
 *   HOW  — the chassis picture and one Referral walkable end to end
 *          (engineering/docs/diagrams/system-overview.md).
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — no sentence on this page describes how things stand except
 * canon text carried verbatim; freshness is displayed (the strip's dates),
 * never auto-judged — no age threshold, no color verdicts. The one live call
 * is GET /api/glass, the freshness stamps.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  GLASS_CANON,
  type GlassActivenessSurface,
  type GlassProcess,
  type GlassTraceStep,
} from "@command-center/shared";
import { MermaidDiagram } from "@/components/mermaid-diagram";
import { useGlassStatus } from "@/lib/use-glass";
import { cn } from "@/lib/utils";

const BRIEF_PATH = "engineering/docs/agents/brief.md";

export function GlassPage() {
  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass</h1>
      <p className="mb-1 text-sm text-plane-muted">
        The semantic layer's front door — what the system is, what it does,
        how a Referral moves, rendered read-only from the canon files named
        below.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        Canon lives in <code>{GLASS_CANON.meta.sources.context}</code>,{" "}
        <code>{GLASS_CANON.meta.sources.busMatrix}</code> and{" "}
        <code>{GLASS_CANON.meta.sources.overview}</code>, packaged at build
        time; this page renders it read-only.
      </p>

      <p className="mb-1 text-xs">
        <Link
          to="/engineering/glass/vocabulary"
          className="text-plane-muted underline hover:text-plane"
        >
          Vocabulary — the ratified glossary, ruled-out words, and retired ids
        </Link>
      </p>
      <p className="mb-1 text-xs">
        <Link
          to="/engineering/glass/truth"
          className="text-plane-muted underline hover:text-plane"
        >
          Truth — the register: every front file, verdict, checked_on date
        </Link>
      </p>
      <p className="mb-1 text-xs">
        <Link
          to="/engineering/glass/machine"
          className="text-plane-muted underline hover:text-plane"
        >
          Machine — the trigger map and every non-archived workflow's node flow
        </Link>
      </p>
      <p className="mb-1 text-xs">
        <Link
          to="/engineering/glass/pulse"
          className="text-plane-muted underline hover:text-plane"
        >
          Pulse — drift: the last live-drift run and what moved, every
          committed artifact
        </Link>
      </p>
      <p className="mb-1 text-xs">
        <Link
          to="/engineering/glass/catalog"
          className="text-plane-muted underline hover:text-plane"
        >
          Catalog — catalog-lookup in the browser: every card, its stamp, and
          its typed edges, searchable
        </Link>
      </p>
      <p className="mb-1 text-xs">
        <Link
          to="/engineering/glass/communications"
          className="text-plane-muted underline hover:text-plane"
        >
          Communications — every email and page sent to a Firm's Contact or a
          Potential Client, each beside its example
        </Link>
      </p>
      <p className="mb-5 text-xs">
        <Link
          to="/engineering/glass/stewardship"
          className="text-plane-muted underline hover:text-plane"
        >
          Stewardship — stamps and routes by their measured ratification
          marks, and the new-document path
        </Link>
      </p>

      <FreshnessStrip />
      <IsSection />
      <DoesSection />
      <HowSection />
    </section>
  );
}

/** Freshness displayed, never auto-judged — dates only, no verdicts. */
function FreshnessStrip() {
  const query = useGlassStatus();

  return (
    <div className="mb-6 rounded-md border border-border bg-muted p-4">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        Freshness
      </div>
      {query.isLoading ? (
        <div className="text-sm text-plane-muted" data-testid="glass-strip-loading">
          Loading…
        </div>
      ) : query.isError ? (
        <div className="text-sm text-error" data-testid="glass-strip-error">
          Couldn't load the freshness stamps. It will retry automatically.
        </div>
      ) : query.data !== undefined ? (
        <div className="text-sm" data-testid="glass-strip">
          canon packaged{" "}
          <span className="font-medium">
            {query.data.canonGeneratedAt.slice(0, 10)}
          </span>
          {query.data.sourceCommit !== null ? (
            <> · commit <code>{query.data.sourceCommit.slice(0, 8)}</code></>
          ) : null}
          {" · register "}
          <span className="font-medium">{query.data.register.rowCount}</span>{" "}
          rows ({query.data.register.clean} clean ·{" "}
          {query.data.register.swept} swept), checked{" "}
          {query.data.register.oldestCheckedOn} …{" "}
          {query.data.register.newestCheckedOn}
        </div>
      ) : null}
    </div>
  );
}

function SectionHeading({
  label,
  title,
  source,
}: {
  label: string;
  title: string;
  source: string;
}) {
  return (
    <div className="mb-3 mt-10 first:mt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {label}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-xs text-plane-muted">
        renders <code>{source}</code>
      </p>
    </div>
  );
}

/** IS — the C4 Level 1 Context diagram, verbatim from canon, and the
 *  external-surface stamps (wave glass-8, #809): every activeness.yaml entry
 *  of kind external-surface, rendered beside the diagram — id, ref, stamp,
 *  note, warrant, verified date, all the file's own text. The filter by kind
 *  is the one mechanical derivation; the stamps are NOT interpolated into the
 *  diagram, and nothing here claims an entry corresponds to a box (an
 *  authored id-to-node mapping would fail silently when a box is added — the
 *  list renders beside the picture instead). */
function IsSection() {
  const ring: GlassActivenessSurface[] =
    GLASS_CANON.stewardship.activeness.data.surfaces.filter(
      (s) => s.kind === "external-surface",
    );

  return (
    <>
      <SectionHeading
        label="Is"
        title="The system and everything that touches it"
        source={GLASS_CANON.meta.sources.context}
      />
      <div className="rounded-md border border-border bg-card p-4">
        <MermaidDiagram source={GLASS_CANON.context.mermaid} />
        <details className="mt-3 border-t border-border pt-3">
          <summary className="cursor-pointer text-xs font-medium text-plane-muted">
            Reading notes (verbatim from the canon file)
          </summary>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-plane-muted">
            {GLASS_CANON.context.readingNotes}
          </pre>
        </details>
      </div>
      {ring.length > 0 ? (
        <div
          className="mt-4 rounded-md border border-border bg-card p-4"
          data-testid="glass-ring"
        >
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
            External-surface entries, stamped
          </div>
          <p className="mb-3 text-xs text-plane-muted">
            Every entry of kind <code>external-surface</code> in{" "}
            <code>{GLASS_CANON.stewardship.activeness.sourcePath}</code>,
            carried verbatim — id, ref, stamp, note, warrant and verified date
            as that file writes them.
          </p>
          <ul className="space-y-3">
            {ring.map((s) => (
              <li
                key={s.id}
                className="border-b border-border/60 pb-3 last:border-b-0 last:pb-0"
                data-testid={`glass-ring-${s.id}`}
              >
                <div className="flex flex-wrap items-baseline gap-2 text-sm">
                  <code className="font-medium">{s.id}</code>
                  <span className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-plane-muted">
                    {s.stamp}
                  </span>
                  {s.verified !== undefined ? (
                    <span className="text-xs text-plane-muted">
                      verified {s.verified}
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-xs text-plane-muted">{s.ref}</div>
                <p className="mt-1 text-xs">{s.note}</p>
                {s.warrant !== undefined ? (
                  <p className="mt-1 text-[11px] text-plane-muted">
                    warrant: {s.warrant}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/** DOES — the bus matrix as a clickable grid. Cells point at the glossary
 *  (the brief renders it); nothing here defines a term. */
function DoesSection() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { processes, ratified, amended } = GLASS_CANON.busMatrix;

  // Column set = every ratified term any row touches, in first-touch order —
  // a projection of the rows, not an authored list.
  const terms: string[] = [];
  for (const p of processes) {
    for (const t of p.terms) if (!terms.includes(t)) terms.push(t);
  }

  return (
    <>
      <SectionHeading
        label="Does"
        title={`The bus matrix — ${processes.length} business processes (ratified ${ratified}${amended ? `, amended ${amended}` : ""})`}
        source={GLASS_CANON.meta.sources.busMatrix}
      />
      <p className="mb-3 text-xs text-plane-muted">
        Rows are business processes; columns are the ratified terms each
        touches. Click a row for its full sentence and where it runs. Terms
        point at the glossary (<code>{BRIEF_PATH}</code>); nothing here
        defines one.
      </p>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-border text-plane-muted">
              <th className="sticky left-0 bg-card px-2 py-1.5 font-medium">
                Process
              </th>
              {terms.map((t) => (
                <th
                  key={t}
                  className="px-1 py-1.5 text-center font-normal [writing-mode:vertical-rl]"
                >
                  {t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processes.map((p) => (
              <ProcessRow
                key={p.id}
                process={p}
                terms={terms}
                open={openId === p.id}
                onToggle={() => setOpenId(openId === p.id ? null : p.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ProcessRow({
  process,
  terms,
  open,
  onToggle,
}: {
  process: GlassProcess;
  terms: string[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={cn(
          "cursor-pointer border-b border-border/60 hover:bg-muted",
          open && "bg-muted",
        )}
        onClick={onToggle}
        data-testid={`glass-process-${process.id}`}
      >
        <td className="sticky left-0 whitespace-nowrap bg-card px-2 py-1.5 font-medium">
          {process.name}
        </td>
        {terms.map((t) => (
          <td key={t} className="px-1 py-1.5 text-center">
            {process.terms.includes(t) ? "●" : ""}
          </td>
        ))}
      </tr>
      {open ? (
        <tr className="border-b border-border/60 bg-muted/50">
          <td colSpan={terms.length + 1} className="px-3 py-2">
            <p className="mb-1 text-sm">{process.does}</p>
            <p className="text-xs text-plane-muted">
              where: {process.where}
            </p>
            {process.addedBy !== null ? (
              <p className="mt-1 text-xs text-plane-muted">
                added beyond the ruled candidate set: {process.addedBy}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** HOW — the chassis, then one Referral walkable end to end. The walk is a
 *  mechanical projection of the trace diagram's edges (line order), not an
 *  authored sequence. */
function HowSection() {
  const { chassisMermaid, traceMermaid, traceSteps, traceProjection } =
    GLASS_CANON.overview;
  const [stepIndex, setStepIndex] = useState(0);
  const step: GlassTraceStep | undefined = traceSteps[stepIndex];

  return (
    <>
      <SectionHeading
        label="How"
        title="The chassis, and one Referral end to end"
        source={GLASS_CANON.meta.sources.overview}
      />
      <div className="mb-4 rounded-md border border-border bg-card p-4">
        <MermaidDiagram source={chassisMermaid} />
      </div>
      <div className="rounded-md border border-border bg-card p-4">
        <MermaidDiagram source={traceMermaid} />
        <div className="mt-4 border-t border-border pt-3" data-testid="glass-walk">
          <div className="mb-2 flex items-center gap-2 text-xs text-plane-muted">
            <span>
              Walk the trace — arrow {stepIndex + 1} of {traceSteps.length}
              {traceProjection.matched !== traceProjection.arrowLines
                ? ` (${traceProjection.matched} of ${traceProjection.arrowLines} arrow lines projected)`
                : null}
            </span>
            <button
              type="button"
              className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              disabled={stepIndex === 0}
            >
              ← prev
            </button>
            <button
              type="button"
              className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              onClick={() =>
                setStepIndex((i) => Math.min(traceSteps.length - 1, i + 1))
              }
              disabled={stepIndex === traceSteps.length - 1}
            >
              next →
            </button>
          </div>
          {step !== undefined ? (
            <div className="text-sm">
              <span className="font-medium">{step.from.text}</span>
              <span className="text-plane-muted"> → </span>
              <span className="font-medium">{step.to.text}</span>
              {step.label !== null && step.label !== "" ? (
                <div className="mt-1 text-xs text-plane-muted">
                  {step.label}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
